import React, { useEffect, useState } from "react";

import useLocalization from "../../hooks/useLocalization";
import useLocation from "../../hooks/useLocation";
import Loading from "../ui/Loading";

import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import Input from "../elements/Input";
import Select from "../elements/Select";
import Switch from "../elements/Switch";
import EditorToolbar from "./EditorToolbar";

const ProjectLocationEditor = ({ projectId, locationId }) => {
  const { getProjectLocation, updateProjectLocation } = useLocation();
  const { dayOfWeek } = useLocalization();
  const theme = useTheme();

  const [location, setLocation] = useState(null);
  const [dow, setDow] = useState("mon");
  const [week1, setWeek1] = useState(0);
  const [week2, setWeek2] = useState(0);
  const [week3, setWeek3] = useState(0);
  const [week4, setWeek4] = useState(0);
  const [week5, setWeek5] = useState(0);
  const [fields, setFields] = useState([]);
  const [clientCode, setClientCode] = useState("");

  useEffect(() => {
    getData();
  }, [projectId, locationId]);

  const getData = async () => {
    const data = await getProjectLocation(projectId, locationId);

    if (data.dow) {
      setDow(data.dow);
    } else {
      setDow("mon");
    }

    setWeek1(data.week1 ? 1 : 0);
    setWeek2(data.week2 ? 1 : 0);
    setWeek3(data.week3 ? 1 : 0);
    setWeek4(data.week4 ? 1 : 0);
    setWeek5(data.week5 ? 1 : 0);

    setFields(data.fields ? data.fields : []);
    setClientCode(data.clientCode || "");

    setLocation(data);
  };

  const updateField = (fieldIndex, newValue) => {
    setFields((current) => {
      let newFields = [...current];
      newFields[fieldIndex].value = newValue;
      return newFields;
    });
  };

  const handleSaveFrequency = async () => {
    await updateProjectLocation({
      id: location.projectLocationId,
      week1,
      week2,
      week3,
      week4,
      week5,
      dow,
    });
    await getData();
  };

  const handleSaveFields = async () => {
    await updateProjectLocation({ id: location.projectLocationId, fields });
    await getData();
  };

  const handleSaveClientCode = async () => {
    await updateProjectLocation({
      id: location.projectLocationId,
      clientCode: clientCode ? clientCode.trim() : null,
    });
    await getData();
  };

  if (!location) return <Loading />;

  return (
    <>
      <Box>
        <Box
          sx={{ padding: "8px", display: "flex" }}
          gap={1}
        >
          <Box
            sx={{
              padding: "6px",
              boxShadow: theme.shadows[4],
              borderRadius: "6px",
              flex: 1,
            }}
          >
            <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
              {location.projectName}
            </Typography>

            <Typography sx={{ fontSize: "10px" }}>
              {location.syndicationId ? "Syndicated" : "Not Syndicated"}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                sx={{ fontSize: "12px", fontWeight: "bold", marginTop: "8px" }}
              >
                {`${location.name} ${
                  location.locationNumber ? `#${location.locationNumber}` : ``
                }`}
              </Typography>
              <Typography sx={{ fontSize: "8px" }}>
                {location.locationId}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "11px" }}>
              {location.streetAddress}
            </Typography>
            {location.otherAddress && (
              <Typography sx={{ fontSize: "11px" }}>
                {location.otherAddress}
              </Typography>
            )}
            <Typography
              sx={{ fontSize: "11px" }}
            >{`${location.city}, ${location.province}`}</Typography>
            <Typography sx={{ fontSize: "11px" }}>{location.postal}</Typography>

            <Typography
              sx={{ marginTop: "8px", fontSize: "12px", fontWeight: "bold" }}
            >
              {location.territory}
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {location.repName}
            </Typography>
          </Box>

          <Box
            sx={{
              boxShadow: theme.shadows[4],
              borderRadius: "6px",
              flex: 1,
            }}
          >
            <Typography
              sx={{
                paddingLeft: "8px",
                paddingTop: "8px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Frequency
            </Typography>

            <EditorToolbar onSave={handleSaveFrequency} />

            <Box sx={{ padding: "8px" }}>
              <Box sx={{ width: "120px" }}>
                <Select
                  label="Day of Week"
                  value={dow}
                  onChange={(newValue) => setDow(newValue)}
                  items={dayOfWeek}
                />
              </Box>

              <Box
                sx={{ marginTop: "8px", display: "flex" }}
                gap={1}
              >
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px" }}> Week 1</Typography>
                  <Switch
                    value={week1}
                    onChange={(newValue) => setWeek1(newValue ? 1 : 0)}
                  />
                </Box>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px" }}> Week 2</Typography>
                  <Switch
                    value={week2}
                    onChange={(newValue) => setWeek2(newValue ? 1 : 0)}
                  />
                </Box>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px" }}> Week 3</Typography>
                  <Switch
                    value={week3}
                    onChange={(newValue) => setWeek3(newValue ? 1 : 0)}
                  />
                </Box>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px" }}> Week 4</Typography>
                  <Switch
                    value={week4}
                    onChange={(newValue) => setWeek4(newValue ? 1 : 0)}
                  />
                </Box>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px" }}> Week 5</Typography>
                  <Switch
                    value={week5}
                    onChange={(newValue) => setWeek5(newValue ? 1 : 0)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ padding: "8px", marginTop: "8px" }}>
          <Box
            sx={{
              boxShadow: theme.shadows[4],
              borderRadius: "6px",
              flex: 1,
            }}
          >
            <Typography
              sx={{
                paddingLeft: "8px",
                paddingTop: "8px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Client Code
            </Typography>

            <EditorToolbar onSave={handleSaveClientCode} />

            <Box sx={{ padding: "8px" }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Input
                  label="Client Code"
                  value={clientCode}
                  onChange={(newValue) => setClientCode(newValue)}
                  width="220px"
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ padding: "8px", marginTop: "8px" }}>
          <Box
            sx={{
              boxShadow: theme.shadows[4],
              borderRadius: "6px",
              flex: 1,
            }}
          >
            <Typography
              sx={{
                paddingLeft: "8px",
                paddingTop: "8px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Custom Fields
            </Typography>

            <EditorToolbar onSave={handleSaveFields} />

            <Box sx={{ padding: "8px" }}>
              {fields.length === 0 ? (
                <Typography sx={{ fontSize: "12px" }}>
                  No custom fields set
                </Typography>
              ) : (
                <Box sx={{ display: "flex" }}>
                  {fields.map((field, index) => (
                    <Input
                      label={field.name}
                      value={field.value}
                      onChange={(newValue) => updateField(index, newValue)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ProjectLocationEditor;
